import { ChangeDetectionStrategy, Component, ElementRef, afterNextRender, inject, viewChild } from '@angular/core';
import { BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideImage, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { pluralRu } from '@lite-workspace/shared/format';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { ImportListComponent, IMPORT_LIST_ROW_CLASS, type ImportListColumn } from './import-list.component';

export type FileAlreadyExistsContext = {
  /** Папка человека, куда фотограф добросил отдельные файлы; без неё речь идёт о галерее целиком. */
  folder?: string;
  /** Имена файлов, которые в этой папке уже лежат. */
  duplicates: string[];
  /** Сколько из брошенных файлов в папке ещё нет. */
  fresh: number;
};

/** Слово человека: залить только новое, перезаписать совпавшие или ничего не делать. */
export type FileAlreadyExistsResult = 'skip' | 'replace' | undefined;

const COLUMNS: readonly ImportListColumn[] = [{ label: 'Файл' }];
const TRACKS = 'minmax(0,1fr)';

/**
 * «Эти фото уже есть в папке» — файловый ответ на добрасывание отдельных кадров в папку человека.
 * Чистая витрина: имена совпавших файлов и одно решение на всю пачку; ничего не считает и никуда
 * не ходит — состав готовит вызывающий.
 */
@Component({
  selector: 'app-file-already-exists-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, DialogSheetComponent, ImportListComponent, BrnDialogTitle, NgIcon],
  providers: [provideIcons({ lucideImage, lucideTriangleAlert })],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <div class="mb-5 flex items-center gap-4">
          <span class="grid size-14 flex-none place-items-center rounded-[var(--r-l)] bg-warn-soft text-warn">
            <ng-icon name="lucideTriangleAlert" size="28" />
          </span>
          <h2 brnDialogTitle class="min-w-0 text-lg leading-snug font-bold text-ink">{{ title }}</h2>
        </div>

        <app-import-list [columns]="columns" [tracks]="tracks">
          <!-- Ключ по порядку: список статичен, а одноимённых файлов в одной папке диск не даёт. -->
          @for (name of duplicates; track $index) {
            <div [class]="rowClass" [style.grid-template-columns]="tracks" data-testid="file-already-exists-row">
              <span class="flex min-w-0 items-center gap-2">
                <ng-icon name="lucideImage" size="16" class="shrink-0 text-c3" />
                <span class="block truncate text-ink">{{ name }}</span>
              </span>
            </div>
          }
        </app-import-list>

        <div class="mt-6 flex flex-col-reverse gap-3 md:flex-row">
          <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="close('replace')">
            {{ replaceLabel }}
          </button>
          @if (fresh > 0) {
            <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="close(undefined)">
              Отмена
            </button>
            <button hlmBtn type="button" #confirmButton class="h-12 flex-1 justify-center" (click)="close('skip')">
              Загрузить {{ fresh }} {{ freshWord }}
            </button>
          } @else {
            <button hlmBtn type="button" #confirmButton class="h-12 flex-1 justify-center" (click)="close(undefined)">
              Отмена
            </button>
          }
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class FileAlreadyExistsDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('m', 'p-9');
  private readonly ctx = injectBrnDialogContext<FileAlreadyExistsContext>();
  private readonly dialogRef = inject(BrnDialogRef);

  private readonly confirmButton = viewChild<ElementRef<HTMLButtonElement>>('confirmButton');

  protected readonly columns = COLUMNS;
  protected readonly tracks = TRACKS;
  protected readonly rowClass = IMPORT_LIST_ROW_CLASS;

  protected readonly duplicates: readonly string[] = this.ctx.duplicates;
  protected readonly fresh: number = this.ctx.fresh;
  protected readonly freshWord: string = pluralRu(this.fresh, ['новое', 'новых', 'новых']);

  /** Без папки речь про всю галерею — заголовок называет её, а не чужую папку. */
  private readonly where: string = this.ctx.folder ? `в папке «${this.ctx.folder}»` : 'в галерее';

  protected readonly title: string =
    this.fresh > 0
      ? `${this.duplicates.length} из ${this.duplicates.length + this.fresh} фото уже есть ${this.where}`
      : this.duplicates.length === 1
        ? `Это фото уже есть ${this.where}`
        : `${this.duplicates.length} фото уже есть ${this.where}`;

  protected readonly replaceLabel: string = this.fresh > 0 ? `Перезаписать ${this.duplicates.length}` : 'Перезаписать';

  constructor() {
    // Кнопка живёт в ng-template листа: до первого рендера её нет, а фокус-ловушка забирает первую в DOM.
    afterNextRender(() => this.confirmButton()?.nativeElement.focus());
  }

  protected close(result: FileAlreadyExistsResult): void {
    this.dialogRef.close(result);
  }
}
